import {ArtfactKind, DbEntity, FieldEntity, FieldModelXrefEntity, FieldTypeKind, Metadata, ModelDbXrefEntity} from '@mrp-brasil/nick-data-commons';
import {BaseHook, Hook, HookClass, NickProcessArtfact} from '@mrp-brasil/nick-process-commons';
import fs from 'fs';
import os from 'os';
import {from, Observable, Observer, of} from 'rxjs';
import {Client, types} from 'cassandra-driver';
import * as sigV4 from 'aws-sigv4-auth-cassandra-plugin';
import {concatMap, delay, map, toArray} from 'rxjs/operators';
import {v4 as uuid} from 'uuid';
import {AwsS3Hook} from '../../aws/aws.s3.hook';

@HookClass('database.cassandra', '1611fa9f-ceaa-4316-99c5-3aebee1aba1b')
export class DatabaseCassandraHook extends BaseHook {

    private static connections = new Map<string, Client>();
    private static connectionByModel = new Map<string, string>();

    @Hook('8f4cf88c-1b40-46f8-ac6b-bacaed14f81f')
    public authenticate(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return of(inputArtifact)
            .pipe(concatMap(() => {
                if (!inputArtifact.object.dbId)
                    throw new Error('Invalid Database ID.');

                if (DatabaseCassandraHook.connections.has(inputArtifact.object.dbId))
                    return of(DatabaseCassandraHook.connections.get(inputArtifact.object.dbId));

                return this.service('DbService')
                    .pipe(concatMap((service: any) => {
                        return service.findOne(inputArtifact.object.dbId)
                            .pipe(map((dbEntity: DbEntity) => {
                                dbEntity.accessKey$ = service.getAccessKey(dbEntity);
                                return dbEntity;
                            }));
                    }))
                    .pipe(concatMap((dbEntity: DbEntity) => {
                        return dbEntity.accessKey$
                            .pipe(concatMap((access: any) => {

                                let accessConnect$ = of(access);
                                if (access.secureConnectBundle) {
                                    accessConnect$ = this.make(AwsS3Hook)
                                        .pipe(concatMap((s3Hook: AwsS3Hook) => {
                                            const downloadArtifact = new NickProcessArtfact();
                                            downloadArtifact.kind = ArtfactKind.ANY;
                                            downloadArtifact.object = {data: {key: access.secureConnectBundle}};

                                            return s3Hook.downloadToBuffer(downloadArtifact)
                                                .pipe(concatMap((downloadArtifact: NickProcessArtfact) => {
                                                    access.secureConnectBundle = `${os.tmpdir()}/${access.key}.zip`;

                                                    let writeStream = fs.createWriteStream(access.secureConnectBundle);

                                                    return new Observable((observer: Observer<any>) => {
                                                        writeStream.write(downloadArtifact.object, (err) => {
                                                            if (err) {
                                                                observer.error(err);
                                                                observer.complete();
                                                                return;
                                                            }

                                                            observer.next(access);
                                                            observer.complete();
                                                        });
                                                    });
                                                }));
                                        }));
                                }

                                if (access.sigV4AuthProvider) {
                                    access.authProvider = new sigV4.SigV4AuthProvider(access.sigV4AuthProvider);
                                    accessConnect$ = this.make(AwsS3Hook)
                                        .pipe(concatMap((s3Hook: AwsS3Hook) => {
                                            const downloadArtifact = new NickProcessArtfact();
                                            downloadArtifact.kind = ArtfactKind.ANY;
                                            downloadArtifact.object = {data: {key: access.sslOptions.ca}};

                                            return s3Hook.downloadToBuffer(downloadArtifact)
                                                .pipe(concatMap((downloadArtifact: NickProcessArtfact) => {
                                                    access.sslOptions.ca = `${os.tmpdir()}/${access.key}`;

                                                    let writeStream = fs.createWriteStream(access.sslOptions.ca);

                                                    return new Observable((observer: Observer<any>) => {
                                                        writeStream.write(downloadArtifact.object, (err) => {
                                                            if (err) {
                                                                observer.error(err);
                                                                observer.complete();
                                                                return;
                                                            }

                                                            access.sslOptions.ca = [fs.readFileSync(access.sslOptions.ca, 'utf-8')];

                                                            observer.next(access);
                                                            observer.complete();
                                                        });
                                                    });
                                                }));
                                        }));
                                }

                                return accessConnect$
                                    .pipe(concatMap(() => {

                                        const client: any = new Client({
                                            id: types.Uuid.fromString(dbEntity.id),
                                            cloud: access.secureConnectBundle ? {
                                                secureConnectBundle: access.secureConnectBundle
                                            } : null,
                                            credentials: access.user ? {
                                                username: access.user,
                                                password: access.password
                                            } : null,
                                            pooling: {},
                                            authProvider: access.authProvider ? access.authProvider : null,
                                            sslOptions: access.sslOptions ? access.sslOptions : null,
                                            protocolOptions: access.protocolOptions ? access.protocolOptions : null,
                                            contactPoints: access.contactPoints,
                                            localDataCenter: access.localDataCenter
                                            // keyspace: dbEntity.dbName
                                        });

                                        return this.service('ModelDbXrefService')
                                            .pipe(concatMap((service: any) => {
                                                return service.findModelsByDatabaseId(dbEntity.id);
                                            }))
                                            .pipe(concatMap((models: any[]) => {
                                                client._models = models;
                                                client._modelsMap = new Map<string, any>();
                                                client._dbEntity = dbEntity;

                                                models.forEach((model: any) => {
                                                    DatabaseCassandraHook.connectionByModel.set(model.modelId, dbEntity.id);
                                                    client._modelsMap.set(model.modelId, model.model);
                                                });

                                                // todo: store connection with keyspace in hash key name
                                                DatabaseCassandraHook.connections.set(dbEntity.id, client);

                                                return from(client.connect())
                                                    .pipe(map(() => client));
                                            }));
                                    }));
                            }));
                    }));
            }))
            .pipe(concatMap((client: any) => {
                // const queryArtifact = new NickProcessArtfact();
                // queryArtifact.kind = ArtfactKind.ANY;
                // queryArtifact.object = {
                //     dbId: inputArtifact.object.dbId,
                //     query: `CREATE KEYSPACE IF NOT EXISTS "${client._dbEntity.dbName}" WITH replication = {'class': 'SimpleStrategy', 'replication_factor' : 3}`,
                //     params: []
                // };

                // return this.query(queryArtifact)
                //     .pipe(concatMap(() => {
                const queryArtifact = new NickProcessArtfact();
                queryArtifact.kind = ArtfactKind.ANY;
                queryArtifact.object = {
                    dbId: inputArtifact.object.dbId,
                    query: `USE "${client._dbEntity.dbName}"`,
                    params: []
                };

                return this.query(queryArtifact);
                // }));
            }))
            .pipe(map(() => {
                const outputArtifact = new NickProcessArtfact();
                outputArtifact.kind = ArtfactKind.ANY;
                outputArtifact.object = {success: true};
                return outputArtifact;
            }));
    }

    @Hook('8895c2c0-ec7e-4996-b237-3a48f79c22da')
    public query(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return of(inputArtifact)
            .pipe(concatMap(() => {
                if (!inputArtifact.object.dbId)
                    throw new Error('Invalid modelId');

                if (DatabaseCassandraHook.connections.has(inputArtifact.object.dbId))
                    return of(DatabaseCassandraHook.connections.get(inputArtifact.object.dbId));

                const connectArtifact = new NickProcessArtfact();
                connectArtifact.kind = ArtfactKind.ANY;
                connectArtifact.object = {dbId: inputArtifact.object.dbId};

                return this.authenticate(connectArtifact)
                    .pipe(map(() => DatabaseCassandraHook.connections.get(inputArtifact.object.dbId)));
            }))
            .pipe(concatMap((client: any) => {
                return from(client.execute(inputArtifact.object.query, inputArtifact.object.params));
            }))
            .pipe(map((result: any) => {
                const outputArtifact = new NickProcessArtfact();
                outputArtifact.kind = ArtfactKind.ANY;
                outputArtifact.object = result;
                return outputArtifact;
            }));
    }

    @Hook('0fb2922f-b367-47d8-b1f2-ee8524e173e9')
    public sync(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return of(inputArtifact)
            .pipe(concatMap(() => {
                if (!inputArtifact.object.dbId)
                    throw new Error('Invalid dbId');

                if (DatabaseCassandraHook.connections.has(inputArtifact.object.dbId))
                    return of(DatabaseCassandraHook.connections.get(inputArtifact.object.dbId));

                const connectArtifact = new NickProcessArtfact();
                connectArtifact.kind = ArtfactKind.ANY;
                connectArtifact.object = {dbId: inputArtifact.object.dbId};

                return this.authenticate(connectArtifact)
                    .pipe(map(() => DatabaseCassandraHook.connections.get(inputArtifact.object.dbId)));
            }))
            .pipe(concatMap((client: any) => {
                return from(Array.from(client._modelsMap.values()))
                    .pipe(concatMap((model: any) => {
                        const createArtifact = new NickProcessArtfact();
                        createArtifact.kind = ArtfactKind.ANY;
                        createArtifact.object = {modelId: model.id};

                        return this.createTable(createArtifact);
                    }), toArray());
            }))
            .pipe(map((result: any) => {
                const outputArtifact = new NickProcessArtfact();
                outputArtifact.kind = ArtfactKind.ANY;
                outputArtifact.object = result;
                return outputArtifact;
            }));
    }


    @Hook('4dcbd711-5492-4c66-ad7e-2eafb73d538c')
    public createTable(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return of(inputArtifact)
            .pipe(concatMap(() => {
                if (!inputArtifact.object.modelId)
                    throw new Error('Invalid modelId');

                const connect$ = (dbId: string) => of(inputArtifact)
                    .pipe(concatMap(() => {
                        if (DatabaseCassandraHook.connectionByModel.has(inputArtifact.object.modelId))
                            return of(DatabaseCassandraHook.connections.get(DatabaseCassandraHook.connectionByModel.get(inputArtifact.object.modelId)));

                        const connectArtifact = new NickProcessArtfact();
                        connectArtifact.kind = ArtfactKind.ANY;
                        connectArtifact.object = {dbId};

                        return this.authenticate(connectArtifact);
                    }));


                return this.service('ModelDbXrefService')
                    .pipe(concatMap((service: any) => {
                        return service.findModelDbXrefByModelId(inputArtifact.object.modelId)
                            .pipe(concatMap((modelDbXref: ModelDbXrefEntity) => {
                                // load metadata of model
                                return this.service('ModelService')
                                    .pipe(concatMap((modelService: any) => {
                                        return modelService.buildTableMetadata(modelDbXref.model, 'cassandra')
                                            .pipe(concatMap((metadata: any) => {
                                                return connect$(modelDbXref.dbId)
                                                    .pipe(map(() => {
                                                        const client: any = DatabaseCassandraHook.connections.get(DatabaseCassandraHook.connectionByModel.get(inputArtifact.object.modelId));
                                                        client._modelsMap.get(inputArtifact.object.modelId).metadata = metadata;
                                                        return client;
                                                    }));
                                            }));
                                    }));
                            }));
                    }));
            }))
            .pipe(concatMap((client: any) => {
                const model = client._modelsMap.get(inputArtifact.object.modelId);
                const fields = Object.keys(model.metadata).map((columnName: string) => {
                    const field = model.metadata[columnName];

                    let type: string = null;
                    switch (field.type) {
                        case FieldTypeKind.STRING:
                            type = 'VARCHAR';
                            break;
                        case FieldTypeKind.GUID:
                            type = 'UUID';
                            break;
                        case FieldTypeKind.BOOLEAN:
                            type = 'BOOLEAN';
                            break;
                        case FieldTypeKind.DATE:
                            type = 'TIMESTAMP';
                            break;
                        case FieldTypeKind.LONG:
                            type = 'INT';
                            break;
                        case FieldTypeKind.ANY:
                            break;
                        case FieldTypeKind.MODEL:
                            break;
                        case FieldTypeKind.VIRTUAL:
                            break;
                        case FieldTypeKind.ENUM:
                            type = 'DECIMAL';
                            break;
                        case FieldTypeKind.HTML:
                            type = 'TEXT';
                            break;
                        case FieldTypeKind.MONEY:
                            type = 'DECIMAL';
                            break;
                        case FieldTypeKind.DECIMAL:
                            type = 'DECIMAL';
                            break;
                        case FieldTypeKind.TELEPHONE:
                            type = 'VARCHAR';
                            break;
                        case FieldTypeKind.TEXT:
                            type = 'TEXT';
                            break;
                        case FieldTypeKind.POINT:
                            type = 'PointType';
                            break;
                        case FieldTypeKind.FILE:
                            break;
                        case FieldTypeKind.MONGOID:
                            type = 'VARCHAR';
                            break;
                        case FieldTypeKind.BLOB:
                            type = 'BLOB';
                            break;
                        case FieldTypeKind.BIGINT:
                            type = 'BIGINT';
                            break;
                        case FieldTypeKind.SEARCH:
                            break;

                    }

                    return `"${columnName}" ${type}`;
                }).join(', ');

                const primaryKeys = `PRIMARY KEY (${model.settings.primaryKey})`;

                let clustering: string;
                let compacting: string;
                let sufix: string;

                /**
                 * WITH CLUSTERING ORDER BY ("startAt" DESC)
                 *    AND COMPACTION = {'class': 'TimeWindowCompactionStrategy',
                 *             'compaction_window_unit': 'DAYS',
                 *             'compaction_window_size': 1};
                 */

                if (model.settings.clustering)
                    clustering = `CLUSTERING ORDER BY (${model.settings.clustering})`;

                if (model.settings.compacting)
                    compacting = `COMPACTION = ${JSON.stringify(model.settings.compacting).replace(/\"/g, '\'')}`;

                if (clustering && compacting)
                    sufix = `WITH ${clustering} AND ${compacting}`;

                return from(client.execute(`
                    CREATE TABLE IF NOT EXISTS "${model.tableName}"
                    (
                        ${fields},
                        ${primaryKeys}
                    )
                    ${sufix};`, [], {consistency: types.consistencies.localQuorum}));
            }))
            .pipe(map((result: any) => {
                const outputArtifact = new NickProcessArtfact();
                outputArtifact.kind = ArtfactKind.ANY;
                outputArtifact.object = result.rows;
                return outputArtifact;
            }));
    }

    @Hook('1750c5ba-1415-42bd-be06-2620429fe169')
    public findRows(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return of(inputArtifact)
            .pipe(concatMap(() => {
                if (!inputArtifact.object.modelId)
                    throw new Error('Invalid modelId');

                if (DatabaseCassandraHook.connectionByModel.has(inputArtifact.object.modelId))
                    return of(DatabaseCassandraHook.connections.get(DatabaseCassandraHook.connectionByModel.get(inputArtifact.object.modelId)));


                return this.service('ModelDbXrefService')
                    .pipe(concatMap((service: any) => {
                        return service.findModelDbXrefByModelId(inputArtifact.object.modelId)
                            .pipe(concatMap((modelDbXref: ModelDbXrefEntity) => {
                                const connectArtifact = new NickProcessArtfact();
                                connectArtifact.kind = ArtfactKind.ANY;
                                connectArtifact.object = {dbId: modelDbXref.dbId};

                                return this.authenticate(connectArtifact);
                            }))
                            .pipe(concatMap(() => {
                                return of(DatabaseCassandraHook.connections.get(DatabaseCassandraHook.connectionByModel.get(inputArtifact.object.modelId)));
                            }));
                    }));
            }))
            .pipe(concatMap((client: any) => {
                // const model = client._modelsMap.get(inputArtifact.object.modelId);
                // const customAttributes = new Map<string, any>();
                //
                // if (inputArtifact.object.config?.attributes) {
                //     inputArtifact.object.config?.attributes.forEach((attr: string) => {
                //         if (attr.indexOf('|fn:count') >= 0) {
                //             const attribute = attr.replace('|fn:count', '');
                //             customAttributes.set(attribute, {aggregate: 'count'});
                //         } else
                //             customAttributes.set(attr, {});
                //     })
                // }
                //
                // let columns = Object.keys(model.metadata);
                // if (customAttributes.size)
                //     columns = columns.filter((columnName: string) => customAttributes.has(columnName));
                //
                // const fields = columns.map((columnName: string) => {
                //
                //     if (customAttributes.has(columnName) && customAttributes.get(columnName).aggregate == 'count')
                //         return `COUNT("${columnName}") as "count${columnName}"`;
                //
                //     return `"${columnName}"`;
                // }).join(', ');
                //
                // let where: string = '';
                //
                // if (inputArtifact.object.where)
                //     where = 'WHERE ' + inputArtifact.object.where

                return from(client.execute(`${inputArtifact.object.query}`, [], {prepare: true, consistency: types.consistencies.localQuorum}));
            }))
            .pipe(map((result: any) => {
                const outputArtifact = new NickProcessArtfact();
                outputArtifact.kind = ArtfactKind.ANY;
                outputArtifact.object = result.rows;
                return outputArtifact;
            }));
    }

    @Hook('8e4f07af-e721-4d6a-b71f-66fa8babd332')
    public createOrUpdateRow(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return of(inputArtifact)
            .pipe(concatMap(() => {
                if (!inputArtifact.object.modelId)
                    throw new Error('Invalid modelId');

                if (DatabaseCassandraHook.connectionByModel.has(inputArtifact.object.modelId))
                    return of(DatabaseCassandraHook.connections.get(DatabaseCassandraHook.connectionByModel.get(inputArtifact.object.modelId)));


                return this.service('ModelDbXrefService')
                    .pipe(concatMap((service: any) => {
                        return service.findModelDbXrefByModelId(inputArtifact.object.modelId)
                            .pipe(concatMap((modelDbXref: ModelDbXrefEntity) => {
                                const connectArtifact = new NickProcessArtfact();
                                connectArtifact.kind = ArtfactKind.ANY;
                                connectArtifact.object = {dbId: modelDbXref.dbId};

                                return this.authenticate(connectArtifact);
                            }))
                            .pipe(concatMap(() => {
                                return of(DatabaseCassandraHook.connections.get(DatabaseCassandraHook.connectionByModel.get(inputArtifact.object.modelId)));
                            }));
                    }));
            }))
            .pipe(concatMap((client: any) => {
                let model = client._modelsMap.get(inputArtifact.object.modelId);

                let prepareModel$ = of(model);
                if (!model.metadata) {
                    const syncTableArtifact = new NickProcessArtfact();
                    syncTableArtifact.kind = ArtfactKind.ANY;
                    syncTableArtifact.object = {modelId: inputArtifact.object.modelId};
                    prepareModel$ = this.createTable(syncTableArtifact);
                }

                let primaryKeyColumn: null;
                let isInsert: boolean;


                let row: any = inputArtifact.object.data ? inputArtifact.object.data : {};

                const fieldsToUseInWhere: any[] = [];
                const fieldsToUse: any[] = [];
                const params: any = {};

                return prepareModel$
                    .pipe(concatMap(() => {
                        model = client._modelsMap.get(inputArtifact.object.modelId);

                        Object.keys(model.metadata).forEach((columnName: string) => {
                            const field = model.metadata[columnName];

                            if (field.primaryKey) {
                                primaryKeyColumn = field;

                                if (!row.hasOwnProperty(columnName) || !row[columnName]) {
                                    isInsert = true;
                                    row[columnName] = uuid();
                                    fieldsToUse.push({field, columnName});
                                } else {
                                    fieldsToUseInWhere.push({field, columnName});
                                }

                                params[columnName] = row[columnName];
                            } else if (row.hasOwnProperty(columnName)) {
                                fieldsToUse.push({field, columnName});

                                switch (field.type) {
                                    case FieldTypeKind.DATE:
                                        params[columnName] = new Date(row[columnName]);
                                        break;
                                    case FieldTypeKind.STRING:
                                    case FieldTypeKind.GUID:
                                    case FieldTypeKind.BOOLEAN:
                                    case FieldTypeKind.LONG:
                                    case FieldTypeKind.ANY:
                                    case FieldTypeKind.MODEL:
                                    case FieldTypeKind.VIRTUAL:
                                    case FieldTypeKind.ENUM:
                                    case FieldTypeKind.HTML:
                                    case FieldTypeKind.MONEY:
                                    case FieldTypeKind.DECIMAL:
                                    case FieldTypeKind.TELEPHONE:
                                    case FieldTypeKind.TEXT:
                                    case FieldTypeKind.POINT:
                                    case FieldTypeKind.FILE:
                                    case FieldTypeKind.MONGOID:
                                    case FieldTypeKind.BLOB:
                                    case FieldTypeKind.BIGINT:
                                    case FieldTypeKind.SEARCH:
                                        params[columnName] = row[columnName];
                                        break;

                                }
                            }
                        });

                        const fields = fieldsToUse.map((item: any, index: number) => {
                            if (isInsert)
                                return `"${item.columnName}"`;
                            else
                                return `"${item.columnName}" = :${item.columnName}`;
                        }).join(', ');

                        const fieldsWhere = fieldsToUseInWhere.map((item: any, index: number) => {
                            return `"${item.columnName}" = :${item.columnName}`;
                        }).join(', ');

                        /**
                         * INSERT INTO flow_telemetry.flow_execution (id, endat, name, startat)
                         * VALUES (1dc8bb2e-c4f3-4041-8d82-2e5b8e15c833, '2022-07-29 22:42:31.000', 'teste', '2022-07-29 22:42:38.000');
                         */

                        /**
                         * UPDATE flow_telemetry.flow_execution
                         * SET endat   = '2022-07-29 22:42:31.000',
                         *     name    = 'teste',
                         *     startat = '2022-07-29 22:42:38.000'
                         * WHERE id = 1dc8bb2e-c4f3-4041-8d82-2e5b8e15c833;
                         */

                        let ttl: string = '';
                        if (model.settings?.ttl)
                            ttl = `USING TTL ${model.settings?.ttl}`;

                        let query: string;

                        if (isInsert)
                            query = `INSERT INTO "${model.tableName}" (${fields})
                                     VALUES (${fieldsToUse.map((item: any) => `:${item.columnName}`).join(', ')}) ${ttl};`;
                        else
                            query = `UPDATE "${model.tableName}"
                                     SET ${fields}
                                     WHERE ${fieldsWhere} ${ttl}`;

                        return from(client.execute(`${query}`, params, {prepare: true, consistency: types.consistencies.localQuorum}));
                    }));
            }))
            .pipe(map((result: any) => {
                const outputArtifact = new NickProcessArtfact();
                outputArtifact.kind = ArtfactKind.ANY;
                outputArtifact.object = result.rows;
                return outputArtifact;
            }));
    }
}
